/**
 * MarketplacePublishPage — /marketplace/publish
 * Publish a listing on the FormPage template. Demo pages: /form-layouts (sections, footer actions) and /pricing-tables
 * (what a listing shows). Server route publishMarketplaceListing: `RequireSuperadmin`.
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import {
  listMarketplaceListingsQueryKey,
  publishMarketplaceListingMutation,
} from '../../generated/api/@tanstack/react-query.gen.js';
import { zMarketplaceListingCreate } from '../../generated/api/zod.gen.js';
import {
  Form,
  FormActions,
  FormFullWidth,
  FormSection,
  NumberFormField,
  RadioGroupFormField,
  SelectFormField,
  TextAreaFormField,
  TextFormField,
  useZodForm,
} from '../../components/form/index.js';
import { FormPage } from '../../components/page/index.js';
import { Can } from '../../components/auth/Can.js';
import { useToast } from '../../components/ui/Toast.js';

const PRICING_OPTIONS = [
  { value: 'free', label: 'Free' },
  { value: 'one_time', label: 'One-time price' },
  { value: 'subscription', label: 'Subscription' },
];

const ACCESS_OPTIONS = [
  {
    value: 'private',
    label: 'Private',
    description: 'Other organizations can run it. They never see or fork the content.',
  },
  {
    value: 'public',
    label: 'Public',
    description: 'Other organizations can read the content and fork their own copy.',
  },
];

// Tags are typed as one comma-separated line; the API takes a list.
const publishSchema = zMarketplaceListingCreate
  .omit({ tags: true, autoAcquire: true })
  .extend({ tags: z.string().optional() })
  .refine((v) => v.pricingModel === 'free' || (v.priceUsd ?? '') !== '', {
    path: ['priceUsd'],
    message: 'Enter a price',
  });

export const MarketplacePublishPage: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { notify } = useToast();
  const publish = useMutation(publishMarketplaceListingMutation());
  const form = useZodForm(publishSchema, {
    defaultValues: {
      title: '',
      description: '',
      category: '',
      tags: '',
      pricingModel: 'free',
      priceUsd: '',
      contentAccess: 'private',
    },
  });
  const pricingModel = form.watch('pricingModel');

  const submit = async (values: z.output<typeof publishSchema>) => {
    const { tags, ...rest } = values;
    await publish.mutateAsync({
      body: {
        ...rest,
        priceUsd: rest.pricingModel === 'free' ? undefined : rest.priceUsd,
        tags: tags
          ?.split(',')
          .map((tag) => tag.trim())
          .filter(Boolean),
      },
    });
    await queryClient.invalidateQueries({ queryKey: listMarketplaceListingsQueryKey() });
    notify('Listing published.');
    navigate('/marketplace');
  };

  return (
    <FormPage
      title="Publish to the marketplace"
      subtitle="Share a workflow, template, tool or workforce config with other organizations."
      backTo="/marketplace"
      backLabel="Marketplace"
    >
      <Can policy="RequireSuperadmin">
        <Form form={form} onSubmit={submit} guardUnsaved>
          <FormSection title="What you are publishing">
            <NumberFormField
              name="entityId"
              label="Entity number"
              required
              minValue={1}
              formatOptions={{ useGrouping: false }}
              description="Find it on the entity's own page, under Publish to marketplace."
            />
            <TextFormField name="title" label="Title" placeholder="Listing title" required />
            <FormFullWidth>
              <TextAreaFormField
                name="description"
                label="Description"
                placeholder="Describe what it does and why others should use it."
                rows={3}
              />
            </FormFullWidth>
            <TextFormField name="category" label="Category" placeholder="Trading, Research" />
            <TextFormField name="tags" label="Tags" placeholder="Comma-separated" />
          </FormSection>
          <FormSection title="Access and price" className="mt-8">
            <FormFullWidth>
              <RadioGroupFormField name="contentAccess" label="Who can see the content" options={ACCESS_OPTIONS} />
            </FormFullWidth>
            <SelectFormField name="pricingModel" label="Pricing" options={PRICING_OPTIONS} />
            {pricingModel !== 'free' && <TextFormField name="priceUsd" label="Price (USD)" placeholder="0.00" />}
            <FormActions submitLabel="Publish" cancelTo="/marketplace" />
          </FormSection>
        </Form>
      </Can>
    </FormPage>
  );
};

export default MarketplacePublishPage;
